<template>
  <div class="customer-picker">
    <!-- Update customer (email) -->
    <to-email-input
      v-if="isChangingCustomer"
      v-model:to-email="newEmail"
      :mailbox-id="mailboxId"
      @update-to-email="updateCustomer"
      @update-canceled="handleCancelCustomerUpdate"
    />
    <!-- Update customer name -->
    <template v-else-if="isUpdatingCustomerName">
      <div class="customer-picker__name-input-wrapper">
        <input
          ref="nameInput"
          v-model="newName"
          :placeholder="`Change name for ${customer.email}...`"
        >
        <div class="submit-options">
          <dd-button
            :is-disabled="!enableChangeCustomerNameSubmit"
            :color="ColorAssistant.MailboxColor"
            size="tiny"
            @click="updateCustomerName"
          >Update name</dd-button>
          <dd-button type="text" size="tiny" @click="handleCancelCustomerNameUpdate">Cancel</dd-button>
        </div>
      </div>
      <div v-if="isNewNameActuallyAnEmail" class="customer-picker__name-woops-address">
        <i class="ddi ddi-warning" />
        Woops! That's an email address. 
        <span @click="handleUpdateCustomerViaWoopsMessaging">Click here to set the customer to <strong>{{newName}}</strong>.</span>
      </div>
    </template>
    <!-- READ-ONLY -->
    <template v-else>
      <!-- Normal user -->
      <dropdown
        v-if="$permissions.isNormalUserOrGuest"
        :force-visibility="showDropdown" 
        placement="bottom-start" 
        title="Customer"
      >
        <customer-picker-display :customer="customer" show-caret-down />
        <template #content>
          <div>
            <ul>
              <li v-if="hasOtherConversations" @click="handleOtherConversationsClick">
                <i class="ddi ddi-conversation" />
                <span v-html="otherConversationsLabel"/>
              </li>
              <li @click="handleChangeCustomerClick">
                <i class="ddi ddi-people" />
                <span><strong>Change the customer</strong> for this conversation</span>
              </li>
              <li :class="emailClipboardClass" :data-clipboard-text="customer.email" @click="closeDropdown">
                <i class="ddi ddi-copy-paste" />
                <span><strong>Copy email address</strong> to clipboard</span>
              </li>
            </ul>
          </div>
        </template>
      </dropdown>
      <!-- Admin/owner -->
      <dropdown 
        v-else 
        :force-visibility="showDropdown" 
        placement="bottom-start" 
        title="Customer"
      >
        <customer-picker-display :customer="customer" show-caret-down />
        <template #content>
          <div>
            <ul>
              <li v-if="hasOtherConversations" @click="handleOtherConversationsClick">
                <i class="ddi ddi-conversation" />
                <span v-html="otherConversationsLabel"/>
              </li>
              <li @click="handleUpdateCustomerNameClick">
                <i class="ddi ddi-edit" />
                <span><strong>Update name</strong> for &lt;{{ customer.email }}&gt;</span>
              </li>
              <li @click="handleChangeCustomerClick">
                <i class="ddi ddi-people" />
                <span><strong>Change the customer</strong> for this conversation</span>
              </li>
              <li :class="emailClipboardClass" :data-clipboard-text="customer.email" @click="closeDropdown">
                <i class="ddi ddi-copy-paste" />
                <span><strong>Copy email address</strong> to clipboard</span>
              </li>
            </ul>
          </div>
        </template>
      </dropdown>
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent, markRaw } from 'vue'
import ClipboardJS from 'clipboard'

/* Mixins */

/* Components */
import ToEmailInput from './to-email-input.vue'
import CustomerPickerDisplay from './customer-picker-display.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */

/* API */
import {
  ILinkedItemForListing,
  IPersonWithEmail,
} from '@/api/types/response.types'

export default defineComponent({
  name: 'CustomerPicker',

  components: {
    ToEmailInput,
    CustomerPickerDisplay,
  },

  props: {
    mailboxId: {
      type: Number,
      required: true,
    },
    customer: {
      type: Object as () => IPersonWithEmail,
      required: true,
    },
    otherConversations: {
      type: Array as () => ILinkedItemForListing[],
      required: true,
    },
  },

  emits: [
    'customer-name-updated',
    'customer-updated',
    'other-conversations-clicked',
  ],

  data() {
    return {
      ColorAssistant,
      newName: '',
      isUpdatingCustomerName: false,

      newEmail: '',
      isChangingCustomer: false,
      enableChangeCustomerSubmit: false,

      showDropdown: false,
      emailClipboard: null as any,
      emailClipboardClass: 'copy-email-to-clipboard',
    }
  },

  computed: {
    enableChangeCustomerNameSubmit(): boolean {
      return (
        this.newName !== '' &&
        !this.isNewNameActuallyAnEmail &&
        this.customer.name !== this.newName
      )
    },
    isNewNameActuallyAnEmail(): boolean {
      return TextAssistant.isValidEmail(this.newName)
    },
    hasOtherConversations(): boolean {
      return this.otherConversations.length > 0
    },
    otherConversationsLabel(): string {
      const convoCount = this.otherConversations.length
      if (convoCount === 1) {
        return '<strong>View</strong> 1 other conversation'
      }
      return `<strong>View</strong> ${convoCount} other conversations`
    },
  },
  watch: {
    customer: {
      immediate: true,
      handler(newVal: IPersonWithEmail) {
        this.resetNewName()
      },
    },
  },
  mounted() {
    this.emailClipboard = markRaw(new ClipboardJS('.' + this.emailClipboardClass))
    this.emailClipboard.on('success', (e: any) => {
      // @ts-ignore
      this.$messaging.showSuccess('Copied email address to your clipboard!')
      e.clearSelection()
    })
  },

  beforeUnmount() {
    this.emailClipboard.destroy()
  },

  methods: {
    closeDropdown() {
      this.showDropdown = true
      this.$nextTick(() => {
        this.showDropdown = false
      })
    },
    resetNewName() {
      // If name is already an email (via server-side defaults), leave it blank.
      // This prevents "woops" alert from firing.
      this.newName = ''
      if (!TextAssistant.isValidEmail(this.customer.name)) {
        this.newName = this.customer.name
      }
    },

    handleCancelCustomerUpdate() {
      this.isChangingCustomer = false
      this.newEmail = ''
    },

    handleCancelCustomerNameUpdate() {
      this.isUpdatingCustomerName = false
      this.resetNewName()
    },

    updateCustomerName() {
      if (this.customer.name !== this.newName) {
        this.$emit('customer-name-updated', this.newName)
      }
      this.isUpdatingCustomerName = false
    },

    updateCustomer() {
      if (this.customer.email !== this.newEmail) {
        this.$emit('customer-updated', this.newEmail)
      }
      this.isChangingCustomer = false
    },

    async handleUpdateCustomerNameClick() {
      this.isUpdatingCustomerName = true
      await this.$nextTick()
      // @ts-ignore
      this.$refs.nameInput.focus()
    },

    handleUpdateCustomerViaWoopsMessaging() {
      this.newEmail = this.newName
      this.updateCustomer()
      this.isUpdatingCustomerName = false
      this.newName = this.customer.name
    },

    async handleChangeCustomerClick() {
      this.isChangingCustomer = true
      this.newEmail = ''
      await this.$nextTick()
    },

    async handleOtherConversationsClick() {
      this.closeDropdown()
      this.$emit('other-conversations-clicked')
    },
  },
})
</script>

<style lang="scss">
.customer-picker {
  width: 100%;
  padding-bottom: 4px;
  margin-left: -6px;
  position: relative;

  &__name {
    &-input-wrapper {
      display: flex;
      align-items: center;

      @include media-breakpoint-down(sm) {
        flex-direction: column;
      }

      input {
        width: 300px;
        height: 30px;
        outline: none;
        @include lettering--p-sm;

        @include media-breakpoint-down(sm) {
          width: 100%;
        }

        padding-left: 11px;
        border-radius: 48px;
        border: $border--solid-light;
      }

      .dd-button:first-of-type {
        margin-left: 10px;
      }
    }

    &-woops-address {
      @include lettering--p-sm;
      color: $color--red;
      padding: 10px 0 0 4px;

      span {
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }

  .customer-picker-display {
    cursor: pointer;
    &:hover {
      background-color: var(--color-background-secondary);
    }
  }

  .submit-options {
    display: flex;

    @include media-breakpoint-down(sm) {
      margin-top: 10px;
      width: 100%;
      justify-content: flex-end;
    }
  }
}
</style>
